import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ImpersonationProvider } from "./context/ImpersonationContext";
import LandingPage from "./LandingPage";
import PricingPage from "./PricingPage";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
import SLASign from "./SLASign";
import Login from "./Login/Login";
import DashboardLayout from "./layout/DashboardLayout";

// 🧭 Client Pages
import ClientDashboard from "./page/ClientDashboard";
import ClientInventory from "./client/inventory/ClientInventory";
import StockReconciliation from "./client/StockReconciliation";
import ChatBox from "./components/msg/Chatbox";
import CustomsDocs from "./client/CustomsDocs";
import CreateShipment from "./client/CreateShipment";
import ClientOrderAdmin from "./client/ClientOrderAdmin";
import GeneralHistory from "./client/GeneralHistory";
import MasterData from "./client/MasterData";
import PayableAccounts from "./client/PayableAccounts";
import MyTickets from "./client/MyTickets";

import ApiPartners from "./client/ApiPartners";
import Workflow from "./client/Workflow";
import ATTAccreditation from "./client/ATTAccreditation";
import LandingEvaluations from "./client/landing-evaluations";
import ClientHSEvaluations from "./client/hs-evaluations/ClientHSEvaluations";
import DevMonitorTab from "./components/DevMonitorTab";
import RolePlaceholder from "./components/RolePlaceholder";
import WMSManager from "./components/3pl/WMSManager";
import CustomsDashboard from "./components/agent/CustomsDashboard";
import CustomsEntryManager from "./components/agent/CustomsEntryManager";
import AgentDashboard from "./components/agent/AgentDashboard";
import ATTApplicationReview from "./components/agent/ATTApplicationReview";
import AgentLandingEvaluations from "./components/agent/AgentLandingEvaluations";
import SuperAdminDashboard from "./components/admin/SuperAdminDashboard";
import SuperAdminHSCodes from "./components/admin/SuperAdminHSCodes";
import SubscriptionManager from "./components/admin/SubscriptionManager";
import RevenueAnalytics from "./components/admin/RevenueAnalytics";
import GlobalAudit from "./components/admin/GlobalAudit";
import ControlTower from "./components/admin/ControlTower";
import SMEProspector from "./components/admin/SMEProspector";
import SystemHealth from "./components/admin/SystemHealth";
import TrafficAnalytics from "./components/admin/TrafficAnalytics";
import ManagementHub, { ManagementRedirect } from "./components/admin/ManagementHub";
import HasbroGuard from "./components/hasbro/HasbroGuard";
import HasbroCustomerGuard from "./components/hasbro/HasbroCustomerGuard";
import TicketDesk from "./components/hasbro/TicketDesk";
import HasbroCustomers from "./components/hasbro/HasbroCustomers";
import HasbroIntegrations from "./components/hasbro/HasbroIntegrations";
import HasbroSentEmails from "./components/hasbro/HasbroSentEmails";
import HasbroImportExport from "./components/hasbro/HasbroImportExport";
import HasbroAudit from "./components/hasbro/HasbroAudit";
import SupportTicketsPage from "./components/admin/SupportTicketsPage";
import PlatformAnnouncements from "./components/admin/PlatformAnnouncements";
import SLAManager from "./components/admin/SLAManager";
import ExcelConverter from "./components/admin/ExcelConverter";
import OperationalFlow from "./components/OperationalFlow";
import EmailCenter from "./components/admin/EmailCenter";
import PaymentsInvoices from "./components/admin/PaymentsInvoices";
import ERPSync from "./components/admin/ERPSync";
import Suppliers from "./components/admin/Suppliers";
import PurchaseOrders from "./components/admin/PurchaseOrders";
import Warehouses from "./components/admin/Warehouses";
import FinancialReports from "./components/admin/FinancialReports";
import VendorDashboard from "./components/vendor/VendorDashboard";
import VendorInbound from "./components/vendor/VendorInbound";
import EDIManagement from "./components/admin/EDIManagement";
import IntegrationHub from "./components/admin/IntegrationHub";
import InboundReceiving from "./components/3pl/InboundReceiving";
import ClientInbound from "./components/client/ClientInbound";

// 🧑‍✈️ Operator Pages
import OperatorDashboard from "./page/operator/OperatorDashboard";
import OperatorManageRequests from "./page/operator/OperatorManageRequests";
import OperatorChatPage from "./page/operator/OperatorChatPage";

export default function App() {
  return (
    <ImpersonationProvider>
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* 🌐 Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/sla/sign/:token" element={<SLASign />} />
        <Route path="/login" element={<Login />} />

        {/* 👑 Super Admin Routes */}
        <Route path="/super-admin" element={<DashboardLayout role="super-admin" />}>
          <Route index element={<SuperAdminDashboard />} />
          <Route path="management" element={<ManagementHub />} />
          <Route path="companies" element={<ManagementRedirect base="/super-admin" tab="companies" />} />
          <Route path="tenants" element={<ManagementRedirect base="/super-admin" tab="tenants" />} />
          <Route path="tenant-features" element={<Navigate to="/super-admin/management?tab=tenants" replace />} />
          <Route path="subscriptions" element={<SubscriptionManager />} />
          <Route path="revenue" element={<RevenueAnalytics />} />
          <Route path="health" element={<SystemHealth />} />
          <Route path="traffic" element={<TrafficAnalytics />} />
          <Route path="audit" element={<GlobalAudit />} />
          <Route path="control-tower" element={<ControlTower />} />
          <Route path="users" element={<ManagementRedirect base="/super-admin" tab="users" />} />
          <Route path="hs-codes" element={<SuperAdminHSCodes />} />
          <Route path="tickets" element={<SupportTicketsPage />} />
          <Route path="announcements" element={<PlatformAnnouncements />} />
          <Route path="excel-converter" element={<ExcelConverter />} />
          <Route path="email-center" element={<EmailCenter />} />
          <Route path="payments" element={<PaymentsInvoices />} />
          <Route path="sla" element={<SLAManager />} />
          <Route path="erp-sync" element={<ERPSync />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="warehouses" element={<Warehouses />} />
          <Route path="financial-reports" element={<FinancialReports />} />
          <Route path="edi" element={<EDIManagement />} />
          <Route path="integrations" element={<IntegrationHub />} />
          <Route path="sme-prospector" element={<SMEProspector />} />
        </Route>

        {/* 👤 Client Routes */}
        <Route path="/client" element={<DashboardLayout role="client" />}>
          <Route index element={<ClientDashboard />} />
          <Route path="inventory" element={<ClientInventory />} />
          <Route path="stock-reconciliation" element={<StockReconciliation />} />
          <Route path="customs" element={<CustomsDocs />} />
          <Route path="chat" element={<ChatBox role="Client" chatId="client-operator" />} />
          <Route path="chat-customer" element={<ChatBox role="Client" chatId="client-customer" />} />
          <Route path="create-shipment" element={<CreateShipment />} />
          <Route path="order-admin" element={<ClientOrderAdmin />} />
          <Route path="history" element={<GeneralHistory />} /> {/* Updated to General History */}
          <Route path="master-data" element={<MasterData />} />
          <Route path="payables" element={<PayableAccounts />} />
          <Route path="api-partners" element={<ApiPartners />} />
          <Route path="att-accreditation" element={<ATTAccreditation />} />
          <Route path="landing-evaluations" element={<LandingEvaluations />} />
          <Route path="hs-evaluations" element={<ClientHSEvaluations />} />
          <Route path="tools/workflow" element={<Workflow />} />
          <Route path="support" element={<MyTickets />} />
          <Route path="inbound" element={<ClientInbound />} />

          {/* aliases → canonical */}
          <Route path="payable-accounts" element={<Navigate to="/client/payables" replace />} />
          <Route path="Payable-Accounts" element={<Navigate to="/client/payables" replace />} />
        </Route>

        {/* 🧑‍✈️ Operator Routes */}
        <Route path="/operator" element={<DashboardLayout role="operator" />}>
          <Route index element={<OperatorDashboard />} />
          <Route path="manage-requests" element={<OperatorManageRequests />} />
          <Route path="chat" element={<OperatorChatPage />} />
        </Route>

        {/* 🛡️ Admin Routes */}
        <Route path="/admin" element={<DashboardLayout role="admin" />}>
          <Route index element={<SuperAdminDashboard />} />
          <Route path="management" element={<ManagementHub />} />
          <Route path="companies" element={<ManagementRedirect base="/admin" tab="companies" />} />
          <Route path="users" element={<ManagementRedirect base="/admin" tab="users" />} />
          <Route path="tenants" element={<ManagementRedirect base="/admin" tab="tenants" />} />
          <Route path="tenant-features" element={<Navigate to="/admin/management?tab=tenants" replace />} />
          <Route path="subscriptions" element={<SubscriptionManager />} />
          <Route path="all-shipments" element={<RolePlaceholder role="admin" />} />
          <Route path="tickets" element={<SupportTicketsPage />} />
          <Route path="announcements" element={<PlatformAnnouncements />} />
          <Route path="hs-codes" element={<SuperAdminHSCodes />} />
          <Route path="revenue" element={<RevenueAnalytics />} />
          <Route path="audit" element={<GlobalAudit />} />
          <Route path="traffic" element={<TrafficAnalytics />} />
          <Route path="control-tower" element={<ControlTower />} />
          <Route path="health" element={<SystemHealth />} />
          <Route path="excel-converter" element={<ExcelConverter />} />
          <Route path="email-center" element={<EmailCenter />} />
          <Route path="payments" element={<PaymentsInvoices />} />
          <Route path="sla" element={<SLAManager />} />
          <Route path="erp-sync" element={<ERPSync />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="warehouses" element={<Warehouses />} />
          <Route path="financial-reports" element={<FinancialReports />} />
          <Route path="edi" element={<EDIManagement />} />
          <Route path="integrations" element={<IntegrationHub />} />
        </Route>

        {/* 🧑‍💼 Agent Routes */}
        <Route path="/agent" element={<DashboardLayout role="agent" />}>
          <Route index element={<AgentDashboard />} />
          <Route path="att-dashboard" element={<AgentDashboard />} />
          <Route path="att-review/:applicationId" element={<ATTApplicationReview />} />
          <Route path="customs" element={<CustomsDashboard />} />
          <Route path="entries" element={<CustomsEntryManager />} />
          <Route path="landing-evaluations" element={<AgentLandingEvaluations />} />
          <Route path="dg-shipments" element={<RolePlaceholder role="agent" />} />
          <Route path="att-projects" element={<ATTAccreditation />} />
          <Route path="compliance" element={<RolePlaceholder role="agent" />} />
          <Route path="support" element={<MyTickets />} />
        </Route>

        {/* 📦 3PL Routes */}
        <Route path="/3pl" element={<DashboardLayout role="3pl" />}>
          <Route index element={<RolePlaceholder role="3pl" />} />
          <Route path="inventory" element={<ClientInventory />} />
          <Route path="wms" element={<WMSManager />} />
          <Route path="ops" element={<RolePlaceholder role="3pl" />} />
          <Route path="inbound" element={<InboundReceiving />} />
        </Route>

        {/* 🏭 Vendor Routes */}
        <Route path="/vendor" element={<DashboardLayout role="vendor" />}>
          <Route index element={<VendorDashboard />} />
          <Route path="inbound" element={<VendorInbound />} />
        </Route>

        {/* 🧸 Hasbro SME Routes — verified @ap.hasbro.com accounts only */}
        <Route path="/hasbro" element={<HasbroGuard><DashboardLayout role="hasbro" /></HasbroGuard>}>
          <Route index element={<ControlTower tenantOnly />} />
          <Route path="tickets" element={<TicketDesk mode="staff" />} />
          <Route path="customers" element={<HasbroCustomers />} />
          <Route path="emails" element={<HasbroSentEmails />} />
          <Route path="imports-exports" element={<HasbroImportExport />} />
          <Route path="audit" element={<HasbroAudit />} />
          <Route path="integrations" element={<HasbroIntegrations />} />
        </Route>

        {/* 🏬 Hasbro customer portal — logins Hasbro creates for its customers */}
        <Route path="/hasbro-customer" element={<HasbroCustomerGuard><DashboardLayout role="hasbro-customer" /></HasbroCustomerGuard>}>
          <Route index element={<TicketDesk mode="customer" />} />
        </Route>

        {/* 👤 SME Customer Routes */}
        <Route path="/customer" element={<DashboardLayout role="customer" />}>
          <Route index element={<RolePlaceholder role="customer" />} />
          <Route path="shipments" element={<RolePlaceholder role="customer" />} />
          <Route path="alerts" element={<RolePlaceholder role="customer" />} />
        </Route>
      </Routes>
    </Router>
    </ImpersonationProvider>
  );
}
